/**
 * El cuaderno de explotación como DATOS: todo lo que pide el «Modelo de cuaderno
 * de explotación» v9 de la Junta de Andalucía (10/03/2026), apartado a apartado,
 * sin nada de maquetación.
 *
 * Es la frontera entre las dos mitades del cuaderno:
 *   - el motor de datos (CQ2) lo RELLENA desde los partes, el inventario, las
 *     fincas y los datos legales de la empresa;
 *   - el PDF estampado sobre la plantilla oficial (`cuadernoPdf.ts`) y su Excel
 *     hermano (`cuadernoXlsx.ts`) lo LEEN tal cual, sin volver a la base de datos.
 *
 * Reglas de forma (las comprueba el esquema):
 *   - fechas en ISO «aaaa-mm-dd»: el formato «dd/mm/aaaa» lo pone quien imprime;
 *   - cifras como número, nunca como texto con coma;
 *   - los «Nº de orden» de 1.2, 1.3 y 2.1 son enteros ≥ 1 y las columnas
 *     Aplicador, Equipo e Id. parcelas del resto de apartados apuntan a ellos;
 *   - un campo que no se sabe va vacío (undefined/null), NUNCA inventado: el
 *     motor de datos es quien cuenta los huecos y pregunta.
 *
 * Los nombres siguen las columnas del modelo (ver `juntaV9/spec.json`, que dice
 * dónde cae cada una en el papel).
 */
import { z } from "zod";

// ─────────────────────────────── piezas comunes ───────────────────────────────

const texto = z.string().trim().max(2000);
const opt = <T extends z.ZodTypeAny>(s: T) => s.nullish();

/** Fecha ISO «aaaa-mm-dd» (sin hora: el cuaderno es por día). */
export const FechaIso = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "fecha aaaa-mm-dd");

/** Una fecha concreta o un intervalo («Indicar el intervalo de fechas o la fecha concreta»). */
export const Intervalo = z.object({ desde: FechaIso, hasta: opt(FechaIso) });

/** «Nº de orden» de una tabla de información general (1.2, 1.3, 2.1). */
export const NumeroOrden = z.number().int().min(1).max(9999);

/** Id. parcelas: los nº de orden de 2.1, o «TODAS» si se trata toda la explotación. */
export const ParcelasRef = z.union([z.literal("TODAS"), z.array(NumeroOrden).min(1)]);

const numero = z.number().finite();
const positivo = z.number().finite().min(0);

/** Tipo de carné de aplicador (1.2). */
export const Carne = z.enum(["basico", "cualificado", "fumigador", "piloto"]);
/** Tipo de explotación en gestión integrada de plagas (1.4 y 2.1). */
export const TipoGip = z.enum(["AE", "PI", "CP", "Atrias", "AS", "NO"]);
/** Secano (SEC), aspersión (ASP), goteo o localizado (LOC), por gravedad (GRA). */
export const SecanoRegadio = z.enum(["SEC", "ASP", "LOC", "GRA"]);
/** Aire libre (AL), malla (M), cubierta bajo plástico (BP), invernadero (INV). */
export const AireLibreProtegido = z.enum(["AL", "M", "BP", "INV"]);
/** «Indicar buena, regular o mala». */
export const Eficacia = z.enum(["buena", "regular", "mala"]);
export const UnidadDosis = z.enum(["kg/ha", "l/ha"]);
export const UnidadCantidad = z.enum(["kg", "l"]);

export const Dosis = z.object({ valor: positivo, unidad: UnidadDosis });
export const Cantidad = z.object({ valor: positivo, unidad: UnidadCantidad });

/** Producto fitosanitario tal y como se imprime: nombre comercial, sustancia activa corta y nº del MAPA. */
export const Producto = z.object({
  nombreComercial: texto.min(1),
  sustanciaActiva: opt(texto),
  numeroRegistro: opt(texto),
});

/** Referencia SIGPAC de un recinto (2.1 y 9.2). */
export const Sigpac = z.object({
  provincia: opt(texto),
  terminoMunicipal: opt(texto),
  agregado: opt(texto),
  zona: opt(texto),
  poligono: opt(texto),
  parcela: opt(texto),
  recinto: opt(texto),
});

/** Nutrientes N / P₂O₅ / K₂O (kg/ha o UF). */
export const Npk = z.object({ n: opt(numero), p2o5: opt(numero), k2o: opt(numero) });

// ─────────────────────────────── 1. Información general ───────────────────────────────

/** 1.1 Datos generales de la explotación, y titular o representante. */
export const DatosGenerales = z.object({
  fechaApertura: opt(FechaIso),
  razonSocial: opt(texto),
  nif: opt(texto),
  reaNacional: opt(texto),
  reaAutonomico: opt(texto),
  direccion: opt(texto),
  localidad: opt(texto),
  codigoPostal: opt(texto),
  provincia: opt(texto),
  telefonoFijo: opt(texto),
  telefonoMovil: opt(texto),
  email: opt(texto),
  titular: z
    .object({
      nombre: opt(texto),
      nif: opt(texto),
      direccion: opt(texto),
      localidad: opt(texto),
      codigoPostal: opt(texto),
      provincia: opt(texto),
      tipoRepresentacion: opt(texto),
      telefono: opt(texto),
      email: opt(texto),
    })
    .default({}),
});

/** 1.2 Personas o empresas que intervienen en el tratamiento. */
export const Persona = z.object({
  orden: NumeroOrden,
  nombre: texto.min(1),
  nif: opt(texto),
  /** Nº de inscripción en el ROPO o nº de carné. */
  ropo: opt(texto),
  carne: opt(Carne),
  asesor: z.boolean().default(false),
});

/** 1.3 Equipos de aplicación. `roma`: nº ROMA, o nº de censo si no es obligatorio. */
export const Equipo = z.object({
  orden: NumeroOrden,
  descripcion: texto.min(1),
  roma: opt(texto),
  fechaAdquisicion: opt(FechaIso),
  fechaUltimaInspeccion: opt(FechaIso),
});

/** 1.4 Asesor, agrupación o entidad de asesoramiento. */
export const Asesoramiento = z.object({
  nombre: texto.min(1),
  nif: opt(texto),
  identificacion: opt(texto),
  tipoExplotacion: opt(TipoGip),
});

// ─────────────────────────────── 2. Parcelas ───────────────────────────────

/** 2.1 Datos identificativos y agronómicos de las parcelas. */
export const Parcela = z.object({
  orden: NumeroOrden,
  sigpac: Sigpac.default({}),
  usoSigpac: opt(texto),
  superficieSigpacHa: opt(positivo),
  superficieCultivadaHa: opt(positivo),
  especie: opt(texto),
  variedad: opt(texto),
  secanoRegadio: opt(SecanoRegadio),
  aireLibreProtegido: opt(AireLibreProtegido),
  sistemaGip: opt(TipoGip),
});

/** 2.2 Datos medioambientales: captaciones para consumo humano y zonas específicas. */
export const Medioambiental = z.object({
  parcelas: ParcelasRef,
  especie: opt(texto),
  variedad: opt(texto),
  captacionEnParcela: opt(z.boolean()),
  distanciaM: opt(positivo),
  coordenadasUtm: opt(texto),
  denominacion: opt(texto),
  zonaTotalmente: opt(z.boolean()),
  /** Parcialmente en zona específica: SI/NO y, si se conocen, las hectáreas afectadas. */
  zonaParcialmente: opt(z.boolean()),
  zonaParcialmenteHa: opt(positivo),
});

// ─────────────────────────────── 3. Tratamientos fitosanitarios ───────────────────────────────

/** 3.1 Registro de actuaciones fitosanitarias: UNA fila = un parte × un producto. */
export const Tratamiento = z.object({
  parcelas: ParcelasRef,
  especie: opt(texto),
  variedad: opt(texto),
  fechas: Intervalo,
  superficieHa: opt(positivo),
  problema: opt(texto),
  /** Nº de orden en 1.2. */
  aplicador: opt(NumeroOrden),
  /** Nº de orden en 1.3. */
  equipo: opt(NumeroOrden),
  producto: Producto,
  dosis: opt(Dosis),
  eficacia: opt(Eficacia),
  observaciones: opt(texto),
});

/** 3.1 bis Registro por parcela (solo cultivos y superficies con asesoramiento). */
export const TratamientoBis = z.object({
  especie: opt(texto),
  variedad: opt(texto),
  parcelas: ParcelasRef,
  superficieCultivadaHa: opt(positivo),
  superficieTratadaHa: opt(positivo),
  plaga: opt(texto),
  /** Justificación (superación de umbrales, condiciones meteorológicas…). */
  justificacion: opt(texto),
  noQuimica: opt(z.object({ tipoMedida: opt(texto), intensidad: opt(texto), fecha: opt(FechaIso) })),
  quimica: opt(z.object({ producto: opt(Producto), dosis: opt(Dosis), fecha: opt(FechaIso) })),
  eficacia: opt(Eficacia),
  observaciones: opt(texto),
});

/** Cajas de validación del 3.1 bis: se rellena quién y cuándo; la FIRMA nunca (D15). */
export const Validacion = z.object({ asesor: opt(texto), ropo: opt(texto), fecha: opt(FechaIso) });

export const Semilla = z.object({
  fechaSiembra: FechaIso,
  parcelas: ParcelasRef,
  especie: opt(texto),
  variedad: opt(texto),
  superficieHa: opt(positivo),
  cantidadKg: opt(positivo),
  producto: opt(Producto),
});

export const Postcosecha = z.object({
  fecha: FechaIso,
  productoVegetal: texto.min(1),
  problema: opt(texto),
  cantidadTm: opt(positivo),
  producto: opt(Producto),
  cantidad: opt(Cantidad),
});

/** 3.4 locales de almacenamiento y 3.5 medios de transporte comparten columnas. */
export const TratamientoInstalacion = z.object({
  fecha: FechaIso,
  /** Local (tipo y dirección) o vehículo (tipo, modelo y matrícula). */
  objeto: texto.min(1),
  problema: opt(texto),
  volumenM3: opt(positivo),
  producto: opt(Producto),
  cantidad: opt(Cantidad),
});

/**
 * Un registro con la pregunta «APLICA TRATAMIENTO: ⇨ SI ⇨ NO». `aplica` null =
 * sin contestar (no se marca ninguna flecha); con filas y `aplica: false` el
 * motor avisa de la contradicción.
 */
const conAplica = <T extends z.ZodTypeAny>(fila: T) =>
  z.object({ aplica: z.boolean().nullable().default(null), filas: z.array(fila).default([]) });

// ─────────────────────────────── 4 a 10 ───────────────────────────────

/** 4 Análisis de productos fitosanitarios. `material`: vegetal, tierra o agua. */
export const Analisis = z.object({
  fecha: FechaIso,
  material: texto.min(1),
  /** Cultivo o cosecha muestreados; en cultivos, nº de orden de las parcelas. */
  muestreado: opt(texto),
  boletin: opt(texto),
  laboratorio: opt(texto),
  sustancias: opt(texto),
});

/** 5 Cosecha comercializada. */
export const Cosecha = z.object({
  fecha: FechaIso,
  producto: texto.min(1),
  cantidadKg: opt(positivo),
  parcelas: opt(ParcelasRef),
  albaran: opt(texto),
  lote: opt(texto),
  cliente: z
    .object({ nombre: opt(texto), nif: opt(texto), direccion: opt(texto), rgseaa: opt(texto) })
    .default({}),
});

/** 6 Caracterización de suelos. */
export const Suelo = z.object({
  parcelas: ParcelasRef,
  fecha: FechaIso,
  ph: opt(numero),
  materiaOrganicaPct: opt(positivo),
  nitrogenoPct: opt(positivo),
  fosforoPpm: opt(positivo),
  potasioPpm: opt(positivo),
  metales: z
    .object({
      cadmio: opt(positivo),
      cobre: opt(positivo),
      niquel: opt(positivo),
      plomo: opt(positivo),
      zinc: opt(positivo),
      mercurio: opt(positivo),
      cromo: opt(positivo),
    })
    .default({}),
});

/** 7 Fertilización (primera hoja). */
export const Fertilizacion = z.object({
  fechas: Intervalo,
  parcelas: ParcelasRef,
  secanoRegadio: opt(texto),
  especie: opt(texto),
  variedad: opt(texto),
  superficieHa: opt(positivo),
  /** Abonado de fondo, cobertera, enmienda… */
  tipoTratamiento: opt(texto),
  material: opt(texto),
  empresaSuministradora: opt(texto),
  regaNifNima: opt(texto),
  /** Al suelo (voleo/localizada), fertirrigación o foliar. */
  formaAplicacion: opt(texto),
  empresaAplicadora: opt(z.object({ nombre: opt(texto), regfer: opt(texto) })),
});

/** 7 Fertilización (continuación): riqueza, dosis y unidades aportadas/acumuladas. */
export const FertilizacionNutrientes = z.object({
  fechas: Intervalo,
  parcelas: ParcelasRef,
  rendimientoKgHa: opt(positivo),
  riqueza: Npk.default({}),
  materiaOrganica: opt(positivo),
  dosisKgHa: opt(positivo),
  aportadas: Npk.default({}),
  acumuladas: Npk.default({}),
});

/** 7 Metales pesados (solo residuos valorizables). */
export const MetalesResiduo = z.object({
  fechas: Intervalo,
  parcelas: ParcelasRef,
  tipoResiduo: opt(texto),
  dosisKgHa: opt(positivo),
  metales: z
    .object({
      cadmio: opt(positivo),
      cobre: opt(positivo),
      niquel: opt(positivo),
      plomo: opt(positivo),
      zinc: opt(positivo),
      mercurio: opt(positivo),
      cromoVI: opt(positivo),
      arsenico: opt(positivo),
      cromo: opt(positivo),
    })
    .default({}),
});

/** 7.1 Resumen del plan de abonado. */
export const PlanAbonadoFila = z.object({
  parcelas: ParcelasRef,
  fechaElaboracion: opt(FechaIso),
  precedente: z.object({ especie: opt(texto), variedad: opt(texto) }).default({}),
  actual: z.object({ especie: opt(texto), variedad: opt(texto) }).default({}),
  necesidades: Npk.default({}),
});

/** 8 Riego. `sistema`: 1 Gravedad … 6 Goteo … 8 Hidroponía con recirculación. */
export const Riego = z.object({
  parcelas: ParcelasRef,
  superficieHa: opt(positivo),
  sistema: opt(z.number().int().min(1).max(8)),
  fechas: Intervalo,
  volumenM3Ha: opt(positivo),
  volumenAcumuladoM3Ha: opt(positivo),
  nitratosMgL: opt(positivo),
  fosforoMgL: opt(positivo),
});

/** 9.1 Pastoreo extensivo. */
export const Pastoreo = z.object({
  idGrupo: texto.min(1),
  referenciaSigpac: opt(texto),
  fechaInicio: opt(FechaIso),
  fechaFin: opt(FechaIso),
  especieAnimal: opt(texto),
  rega: opt(texto),
  numeroAnimales: opt(z.number().int().min(0)),
});

/** 9.2 Siega sostenible / islas de biodiversidad. Hasta 3 fechas de corte por fila. */
export const Siega = z.object({
  parcelas: ParcelasRef,
  sigpac: Sigpac.default({}),
  superficieSigpacHa: opt(positivo),
  cortes: z.array(FechaIso).max(3).default([]),
  laboreo: opt(FechaIso),
  siembra: opt(FechaIso),
  otrasActividades: opt(texto),
});

/** 9.4 Espacios de biodiversidad en cultivos bajo agua. */
export const BajoAgua = z.object({
  parcelas: ParcelasRef,
  fechaNivelacion: opt(FechaIso),
  fechaSiembraSeco: opt(FechaIso),
  fechaInundacion: opt(FechaIso),
  fechaSeca: opt(FechaIso),
  fechaCaballones: opt(FechaIso),
});

/** 9.5 Cubiertas vegetales en cultivos leñosos. */
export const CubiertaVegetal = z.object({
  parcelas: ParcelasRef,
  fechaEstablecimiento: opt(FechaIso),
  anchuraCubiertaM: opt(positivo),
  anchuraLibreM: opt(positivo),
  /** Mantenimiento por medios mecánicos o ganado: qué y cuándo. */
  mantenimiento: opt(texto),
});

/** 9.6 Cubiertas inertes de restos de poda. */
export const CubiertaInerte = z.object({
  parcelas: ParcelasRef,
  fechaEstablecimiento: opt(FechaIso),
  anchuraCubiertaM: opt(positivo),
  anchuraLibreM: opt(positivo),
});

// ─────────────────────────────── el cuaderno entero ───────────────────────────────

/**
 * `notas`: una línea por apartado que se imprime al pie de SUS hojas (a la
 * izquierda de «Hoja nº»). Es donde va «Sin datos registrados en Ulture Pest»
 * de los apartados 5, 7 y 8 (D19) o cualquier aclaración del motor de datos.
 */
export const SeccionNota = z.enum(["1.2", "2.1", "2.2", "3.1", "3.1bis", "3.2", "3.4", "4", "5", "6", "7", "7.1", "8", "9", "10", "doc"]);

export const CuadernoData = z.object({
  /** Cabecera de cada hoja: «Explotación/ Titular de la explotación», NIF y AÑO («2026» o «2025/26»). */
  cabecera: z.object({ explotacion: opt(texto), nif: opt(texto), anio: opt(texto) }),
  "1.1": DatosGenerales.default({}),
  "1.2": z.array(Persona).default([]),
  "1.3": z.array(Equipo).default([]),
  "1.4": z.array(Asesoramiento).default([]),
  "2.1": z.array(Parcela).default([]),
  "2.2": z.array(Medioambiental).default([]),
  "3.1": z.array(Tratamiento).default([]),
  "3.1bis": z
    .object({
      filas: z.array(TratamientoBis).default([]),
      validacionIntermedia: opt(Validacion),
      validacionFinal: opt(Validacion),
    })
    .default({}),
  "3.2": conAplica(Semilla).default({}),
  "3.3": conAplica(Postcosecha).default({}),
  "3.4": conAplica(TratamientoInstalacion).default({}),
  "3.5": conAplica(TratamientoInstalacion).default({}),
  "4": z.array(Analisis).default([]),
  "5": z.array(Cosecha).default([]),
  "6": z.array(Suelo).default([]),
  "7": z
    .object({
      fechaEntradaVigor: opt(FechaIso),
      aplicaciones: z.array(Fertilizacion).default([]),
      nutrientes: z.array(FertilizacionNutrientes).default([]),
      metalesPesados: z.array(MetalesResiduo).default([]),
    })
    .default({}),
  "7.1": z
    .object({
      planAbonado: opt(z.boolean()),
      asesoramiento: opt(z.boolean()),
      filas: z.array(PlanAbonadoFila).default([]),
    })
    .default({}),
  "8": z.object({ fechaEntradaVigor: opt(FechaIso), riegos: z.array(Riego).default([]) }).default({}),
  "9": z
    .object({
      pastoreo: z.array(Pastoreo).default([]),
      siega: z.array(Siega).default([]),
      bajoAgua: z.array(BajoAgua).default([]),
      cubiertasVegetales: z.array(CubiertaVegetal).default([]),
      cubiertasInertes: z.array(CubiertaInerte).default([]),
    })
    .default({}),
  /** 10 Ayudas asociadas: los tres recuadros remiten a 3, 7 y 8; aquí, líneas libres para cada uno. */
  "10": z
    .object({
      fitosanitarios: z.array(texto).default([]),
      fertilizacion: z.array(texto).default([]),
      riego: z.array(texto).default([]),
    })
    .default({}),
  notas: z.record(SeccionNota, texto).default({}),
});

export type CuadernoData = z.infer<typeof CuadernoData>;
/** Lo que se le puede pasar al generador: los apartados vacíos se pueden omitir. */
export type CuadernoDataInput = z.input<typeof CuadernoData>;
export type Tratamiento = z.infer<typeof Tratamiento>;
export type Persona = z.infer<typeof Persona>;
export type Equipo = z.infer<typeof Equipo>;
export type Parcela = z.infer<typeof Parcela>;
export type ProductoCuaderno = z.infer<typeof Producto>;
export type ParcelasRef = z.infer<typeof ParcelasRef>;
export type SeccionNota = z.infer<typeof SeccionNota>;
